import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteMeeting as deleteMeetingAPI } from '@/api/v4/meetings.api';
import { notify } from '@/ui/snackbar/notify';
import { projectIdState } from '@/features/projects/project.state';
import { companyIdState } from '@/features/companies/overview/company.state';
import { useCompanyFetchMode } from '@/features/companies/overview/company-fetch-context';
import { teamIdState } from '@/features/teams/team.state';
import { QueryKey } from '@/config/query-client';
import { useAtomValue } from 'jotai';

export const useDeleteMeeting = ({
  onRefreshData,
}: {
  onRefreshData: () => void;
}) => {
  const { t } = useTranslation('default');
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();
  const projectId = useAtomValue(projectIdState);
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();
  const teamId = useAtomValue(teamIdState);

  const mutation = useMutation({
    mutationFn: async (meetingId: string) => {
      setIsDeleting(true);
      await deleteMeetingAPI(meetingId);
    },
    onError: (error: Error) => {
      setIsDeleting(false);
      notify({ message: t`default:unknownError` });
      console.error(error.message);
    },
    onSuccess: async () => {
      onRefreshData();
      if (projectId) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Project, projectId],
        });
      }
      if (companyId) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Company, fetchMode, companyId],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.CompanyActionsInfo],
        });
      }
      if (teamId) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKey.Team, teamId],
        });
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.LastActivitiesCount],
        }),
      ]);

      setIsDeleting(false);
    },
  });

  return {
    deleteMeeting: mutation.mutateAsync,
    isDeleting,
    isError: mutation.isError,
  };
};
